/**
 * The Polly session.
 *
 * Nothing more than a JWT the backend hands back after a passkey ceremony, kept under
 * its OWN localStorage key. A polly session and a LIQUIDO team session never meet:
 * the team router guard reads LIQUIDO_JWT and must never see a polly token, or it would
 * try to hydrate a team from it and log the user out.
 *
 * The token lives 14 days (liquido.polly.jwt-expiration-secs), and the polly client renews it before a request once it
 * is more than an hour old (polly-client.js), so a returning voter taps nothing at all - a passkey user once again 90
 * days after the passkey tap, when the backend renews no further. Once it has run out, the backend answers every request that carries it with HTTP 401 - even reading a polly - so an
 * expired token must never be sent: see isSessionExpired() and sessionRejected(), and the router's guard.
 *
 * Two kinds of polly session: a PASSKEY session (a creator, or a friend who registered one) and a
 * GUEST session (a friend who votes without a passkey - it may vote, and nothing else). The backend
 * knows which from the token itself; this module remembers it beside the token, so the pages can
 * decide what to show - offer a passkey, or ask for one - without decoding a JWT.
 *
 * localStorage is not reactive, so every change of the session also bumps a counter that hasSession() reads:
 * a computed or a template that asks "am I signed in?" then runs again when somebody registers a passkey
 * or logs out, instead of showing what was true when the page was opened.
 */
import { ref } from "vue"
import { isJwtExpired } from "@/shared/services/jwt-payload.js"

const POLLY_JWT_KEY = "LIQUIDO_POLLY_JWT"
const POLLY_GUEST_KEY = "LIQUIDO_POLLY_GUEST"

const sessionChanges = ref(0)

export function getSessionJwt() {
	try {
		return localStorage.getItem(POLLY_JWT_KEY) || undefined
	} catch (e) {
		return undefined		// private mode, storage disabled, ...
	}
}

/**
 * @param {String} jwt the polly session from the backend
 * @param {{ guest: Boolean }} options guest: true for a guest session (pollyGuestSession), false for a passkey's
 */
export function setSession(jwt, { guest = false } = {}) {
	if (!jwt) return
	try {
		localStorage.setItem(POLLY_JWT_KEY, jwt)
		if (guest) localStorage.setItem(POLLY_GUEST_KEY, "true")
		else localStorage.removeItem(POLLY_GUEST_KEY)
	} catch (e) {
		console.warn("Polly: cannot persist session", e)
	}
	sessionChanges.value++
}

export function clearSession() {
	try {
		localStorage.removeItem(POLLY_JWT_KEY)
		localStorage.removeItem(POLLY_GUEST_KEY)
	} catch (e) { /* nothing we can do */ }
	sessionChanges.value++
}

/** Is this browser's polly session a guest's - one that may vote, and nothing else? */
export function isGuestSession() {
	try {
		return hasSession() && localStorage.getItem(POLLY_GUEST_KEY) === "true"
	} catch (e) {
		return false
	}
}

/** Does this browser hold a passkey session - the kind that may create, edit and finish a polly? */
export function hasPasskeySession() {
	return hasSession() && !isGuestSession()
}

/** Does this browser already hold a polly session? */
export function hasSession() {
	void sessionChanges.value		// the dependency that makes this reactive
	return !!getSessionJwt()
}

/**
 * Has this browser's polly session run out? Then the backend refuses every request that carries it (HTTP 401).
 * A token whose expiry cannot be read (the mocks' made-up ones) is not called expired.
 */
export function isSessionExpired() {
	const jwt = getSessionJwt()
	return !!jwt && isJwtExpired(jwt)
}

/** What happens when a PASSKEY session has run out: set by router.js - it sends the user to the login page. */
let onPasskeySessionExpired = () => {}

/** @param {Function} handler called when a passkey session turns out to be expired or refused */
export function setPasskeySessionExpiredHandler(handler) {
	onPasskeySessionExpired = handler || (() => {})
}

/**
 * The backend refused this browser's session (HTTP 401 without a polly error code), or it has run out: it is dropped,
 * so it is never sent again. A guest has nothing more to do - no passkey to log in with, and the next vote gets a
 * fresh guest session. A passkey user goes to the handler: the login page asks for the passkey (polly-login.vue).
 */
export function sessionRejected() {
	if (!getSessionJwt()) return
	const wasPasskey = !isGuestSession()
	clearSession()
	if (wasPasskey) onPasskeySessionExpired()
}
